/** @file DrawableSection: one drawable's fields (sheer, z order, shadow, anchor) over drawable.set. */
import type { DrawableFields } from '@zilo/pose3d-commands/types/puppetInputs';
import type { Anchor, Drawable, ImpostorDrawable } from '@zilo/pose3d-format/types/drawable';
import type { ReactElement } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { NumericField } from '../../ui-kit/numericInput/NumericField';
import { NumericRow } from '../../ui-kit/numericInput/NumericRow';
import { InspectorSection } from './InspectorSection';

const row = { display: 'flex', alignItems: 'center', gap: 6 };
const label = { width: 92, color: 'var(--muted, #8b919e)' };

/** The impostor-only fields: cast shadow and the two-point anchor (node, factor, pixel offset). */
function ImpostorFields({ d, nodes, set }: { d: ImpostorDrawable; nodes: string[]; set: (f: DrawableFields) => void }): ReactElement {
  const anchor: Anchor = d.anchor ?? {};
  const setAnchor = (a: Anchor): void => {
    const next: Anchor = { ...anchor, ...a };
    if (next.node === undefined) delete next.node;
    set({ anchor: next });
  };
  return (
    <>
      <label style={row}><span style={label}>shadow</span>
        <input type="checkbox" checked={d.shadow} onChange={(e) => { set({ shadow: e.currentTarget.checked }); }} /></label>
      <label style={row}><span style={label}>anchor node</span>
        <select aria-label="anchor node" value={anchor.node ?? ''} onChange={(e) => { setAnchor({ node: e.currentTarget.value === '' ? undefined : e.currentTarget.value }); }}>
          <option value="">(parent)</option>
          {nodes.map((n) => <option key={n} value={n}>{n}</option>)}
        </select></label>
      <NumericRow label="anchor factor">
        <NumericField label="anchor factor" value={anchor.factor ?? 1} step={0.05} onCommit={(factor) => { setAnchor({ factor }); }} />
      </NumericRow>
      <NumericRow label="anchor offset" unit="px">
        <NumericField label="anchor pixel offset" value={anchor.pixelOffset ?? 0} step={1} onCommit={(pixelOffset) => { setAnchor({ pixelOffset }); }} />
      </NumericRow>
    </>
  );
}

/** Sheer (impostor, effect) and z order (all but decal). */
function DepthFields({ d, set }: { d: Drawable; set: (f: DrawableFields) => void }): ReactElement {
  return (
    <>
      {(d.kind === 'impostor' || d.kind === 'effect') && (
        <NumericRow label="sheer">
          <NumericField label="sheer" value={d.sheer} step={0.1} onCommit={(sheer) => { set({ sheer }); }} />
        </NumericRow>
      )}
      {d.kind !== 'decal' && (
        <NumericRow label="z order">
          <NumericField label="z order" value={d.zOrder} step={1} onCommit={(zOrder) => { set({ zOrder }); }} />
        </NumericRow>
      )}
    </>
  );
}

/** The document's drawable `id` (its offset lives in the Rig block); every edit is one `drawable.set { drawable, fields }`. */
export function DrawableSection({ id }: { id: string }): ReactElement | null {
  const store = useEditorStore();
  const d = useEditor((s) => s.puppet.doc?.puppet.drawables.find((x) => x.id === id));
  const nodes = useEditor((s) => s.puppet.doc?.puppet.nodes);
  if (d === undefined) return null;
  const set = (fields: DrawableFields): void => {
    void store.getState().dispatch([{ name: 'drawable.set', input: { drawable: id, fields } }], `set ${id} ${Object.keys(fields).join(', ')}`);
  };
  const entries = d.kind === 'impostor' || d.kind === 'effect' ? (d.facets ?? []).length : 0;
  return (
    <InspectorSection title={`Drawable ${d.id}`} aside={`${d.kind} on ${d.node}${entries > 0 ? `, ${entries} entries` : ''}`}>
      <DepthFields d={d} set={set} />
      {d.kind === 'impostor' && <ImpostorFields d={d} nodes={Object.keys(nodes ?? {})} set={set} />}
    </InspectorSection>
  );
}
